// Copy helpers for the Astria landing page: body-text lookup, section titles,
// and the React context that hands the active language to every section.

import { createContext, useContext } from "react";
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  type LandingLangCode,
} from "./landingContent";
import {
  BODY_LANG_ORDER,
  BODY_ROWS,
  HEADLINE_TRANSLATIONS,
} from "./landingTranslations";

/** Keys available in HEADLINE_TRANSLATIONS (nav labels, hero copy, section titles). */
export type HeadlineKey =
  | "nMap"
  | "nGuide"
  | "nPlans"
  | "nFaq"
  | "cta"
  | "eyebrow"
  | "sub"
  | "lead"
  | "uniqueT"
  | "fitT"
  | "sysT"
  | "calmT"
  | "innerT"
  | "stepsT"
  | "profileT"
  | "domainsT"
  | "globalT"
  | "privacyT"
  | "plansT"
  | "worldT"
  | "beforeT"
  | "finalT"
  | "finalCta";

const BODY_BY_ENGLISH = new Map<string, readonly string[]>(
  BODY_ROWS.map((row) => [row[0], row]),
);

/** Translates a body string from its English source; falls back to the English text. */
export const translateBody = (text: string, lang: LandingLangCode): string => {
  const column = BODY_LANG_ORDER.indexOf(lang);
  if (column <= 0) return text;
  return BODY_BY_ENGLISH.get(text)?.[column] || text;
};

/** Looks up a title/label for `lang`, falling back to English. */
export const getHeadline = (key: HeadlineKey, lang: LandingLangCode): string =>
  HEADLINE_TRANSLATIONS[lang]?.[key] ||
  HEADLINE_TRANSLATIONS[DEFAULT_LANGUAGE][key] ||
  "";

export interface TextPart {
  text: string;
  emphasis: boolean;
}

/**
 * Splits `a <em>b</em> c` into parts so titles can render the italic accent
 * without dangerouslySetInnerHTML. Only <em> is recognised; anything else is text.
 */
export const parseEmphasis = (markup: string): TextPart[] => {
  const parts: TextPart[] = [];
  const pattern = /<em>(.*?)<\/em>/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(markup)) !== null) {
    if (match.index > cursor) {
      parts.push({ text: markup.slice(cursor, match.index), emphasis: false });
    }
    parts.push({ text: match[1], emphasis: true });
    cursor = match.index + match[0].length;
  }
  if (cursor < markup.length) {
    parts.push({ text: markup.slice(cursor), emphasis: false });
  }
  return parts;
};

export const isLandingLang = (
  code: string | null | undefined,
): code is LandingLangCode => LANGUAGES.some((language) => language.code === code);

/** Language to open the landing page in: the visitor's saved preference, else English. */
export const getInitialLanguage = (): LandingLangCode => {
  try {
    const saved = window.localStorage.getItem("preferredLanguage");
    if (isLandingLang(saved)) return saved;
  } catch {
    // Storage can be blocked (private mode); the default is fine.
  }
  return DEFAULT_LANGUAGE;
};

export interface LandingCopy {
  lang: LandingLangCode;
  /** Translate a body string (card text, labels, FAQ…). */
  tr: (text: string) => string;
  /** Translate a title or label. */
  headline: (key: HeadlineKey) => string;
}

export const createLandingCopy = (lang: LandingLangCode): LandingCopy => ({
  lang,
  tr: (text) => translateBody(text, lang),
  headline: (key) => getHeadline(key, lang),
});

const LandingCopyContext = createContext<LandingCopy>(
  createLandingCopy(DEFAULT_LANGUAGE),
);

export const LandingCopyProvider = LandingCopyContext.Provider;
export const useLandingCopy = (): LandingCopy => useContext(LandingCopyContext);
